import {
  buildTransitionStateProbabilities as buildTransitionStateProbabilitiesForScale,
  isUsableTransitionBand,
  isValidTransition,
  isValidTransitionBandPercent,
} from '@ancestralize/bayesian-utils'

import { getIntervalArrayMonotonicity } from '@/services/db/actions/variable/utils/getIntervalMonotonicity'
import { Monotonicity } from '@/services/models/enums'
import {
  DEFAULT_TRANSITION_HALF_BAND_PERCENT,
  Interval,
  NetworkTransitionDefaults,
  ResolvedTransition,
  TransitionFunction,
} from '@/types/variable'
import {
  addDecimal,
  multiplyDecimal,
  subtractDecimal,
} from '@/utils/general/decimalArithmetic'

// KBE-653 / KBE-612: shared helpers for continuous state-transition nodes.
//
// A semi-continuous variable's transition behavior is one config per adjacent
// state pair. These helpers resolve a stored config into a full curve and build
// the per-state probability vector both the editor chart and diagnosis-time soft
// evidence are computed from (KBE-654).
//
// KBE-769: `variable.transition_functions` is now an OVERRIDE, not the whole
// story. NULL means the variable inherits its network's default, so every
// semi-continuous variable has a resolvable transition function.
//
// KBE-772 replaced the two shapes (`linear` / `sigmoid`) and the symmetric
// `halfBandPercent` with a single shifted log-logistic curve, stated in the
// variable's own units as the values where crossing the boundary is 5% and 95%
// likely. `halfBandPercent` is a parameter of the maths; "95% sure it's diabetes
// by 8.1 mmol/L" is a parameter of the medicine, and researchers can read the
// latter straight off a guideline table.
//
// KBE-817 stored the two DISTANCES either side of the boundary rather than the two
// absolute positions, so a config would travel when its boundary moved underneath
// it. KBE-858 stores all three ABSOLUTE points again -- and the reason the
// distances existed is gone rather than overruled. What made a position stranded
// was that the midpoint lived in another column; the midpoint is stored on the
// entry now, so it moves when a researcher types it and not otherwise, and there
// is nothing left for a boundary move to strand.
//
// So a stored entry is now self-contained, and only an INHERITING variable
// (`transition_functions IS NULL`) reads `intervals` at all -- a percentage needs
// a threshold to resolve against, and that threshold is the boundary. That is why
// `deriveTransitionBoundaries` is still here and why NULL still means inherit.
//
// The network default stays RELATIVE (`default_transition_half_band_percent`)
// because one percentage is scale-appropriate across every variable in the
// network (KBE-768), where one absolute distance is only ever right at one scale.
// A boundary of exactly 0 has no percentage, so an inheriting variable there
// degrades to a hard cutoff and only an override fixes it -- and since the library
// dropped the ownership tie-break, a measurement landing exactly on such a cutoff
// now splits 50/50 rather than snapping. `CAC` is the one row on master in that
// shape; the researchers own moving its midpoint off 0.
//
// KBE-775: a hard cutoff -- no smoothing -- is still expressible per variable. It
// is now three COINCIDING values, where KBE-817 made it a distance that had to stay
// exactly zero through every boundary move and KBE-772 made it a `halfBandPercent`
// of 0. No subtraction is involved any more, so there is no float dust to guard
// against. NULL still means inherit, never off.
//
// KBE-837: the CURVE and the shared predicates now come from
// `@ancestralize/bayesian-utils`, so KBE and Ancestra Health cannot drift into two
// different answers for the same variable. What stays here is what only KBE needs:
// the DECIMAL resolution path, the diff/comparison helpers, and presentation.
//
// KBE-858 removed most of what decimal was protecting. There is no absolute <->
// distance round trip left -- the value typed is the value stored is the value
// redisplayed -- so `transitionFunctionFromValues` is gone and decimal survives on
// exactly two paths, both of which still COMPUTE a number KBE stores and displays:
// the symmetric mirror (`mirrorAboveMidpoint`) and the inherit percentage
// (`resolveAbsoluteBand` / `resolveTransitionFromPercent`). Plain arithmetic makes
// 5% of 5.6 into 0.27999999999999997, and seeding a stored entry from that is what
// used to rewrite every untouched row on the next edit.
//
// The library resolves in plain float and must stay that way; do not route a stored
// or displayed value through it. Its `docs/transitions.md` records the same split
// from the other side.
//
// The symmetrical/skewed distinction is now KBE-ONLY vocabulary (KBE-858): the
// library takes `ResolvedTransition` with all three points required, so the mirror
// is resolved here before any call into the curve and before any export. The
// accepted cost is that the mirror is no longer shared code, so KBE could get it
// wrong where the library once could not -- which is what `isValidTransitionFunction`
// on the write path is for, since a mirrored value landing on the wrong side of the
// midpoint fails bracketing.
//
// `resolveTransitionFunctions` below also keeps reading a stale-length array as
// "inherit" where the library THROWS on one. That divergence is intentional: this
// function feeds render paths, and a throw reached during render takes out the
// `[networkId]` error boundary and permanently clears the AppHeader toolbar
// (gotcha 27). The library is right to throw -- it cannot know whose data it holds
// -- and KBE is right to absorb it here, because its remaining mismatched input is
// the editor's own unsaved state rather than a stored row (KBE-839, and see the
// note on `resolveTransitionFunctions`).

export {
  isUsableTransitionBand,
  isValidTransition,
  isValidTransitionBandPercent,
}

// The boundary between each adjacent pair of states, in the SAME (stored) order as
// `intervals`, so `result[i]` pairs with `transitionFunctions[i]`.
//
// This used to be `deriveTransitionMidpoints`, in the library, and both halves of
// that changed with KBE-858. It is no longer a midpoint -- a stored entry carries
// its own, and this is only the midpoint for an INHERITING variable, whose
// percentage has to resolve against something. And the library no longer has the
// partition to derive it from, so the derivation lives here, with the intervals.
//
// Throws on a non-contiguous partition: a valid partition always shares a bound, so
// a missing one means the intervals are corrupt and any derived boundary would be a
// silent fabrication. Callers that run during render must guard (gotcha 27).
export function deriveTransitionBoundaries(
  intervals: readonly Interval[],
): number[] {
  if (intervals.length <= 1) return []

  return intervals.slice(0, -1).map((interval, i) => {
    const next = intervals[i + 1]
    // `===` makes ABSENCE compare equal to absence, so two intervals that both lack
    // the bound in question would "meet" at `undefined` and yield a NaN midpoint --
    // and the curve then answers a confident [0, 1] for every measurement.
    // `+Infinity === +Infinity` is the same hole with a value in it. An Infinity
    // bound is only rejected where two intervals SHARE it: a partition whose outer
    // ends are unbounded is ordinary, and the shared bound between [-Inf, 5.6) and
    // [5.6, +Inf) is 5.6.
    if (
      Number.isFinite(interval.upperBound) &&
      interval.upperBound === next.lowerBound
    ) {
      return interval.upperBound
    }
    if (
      Number.isFinite(interval.lowerBound) &&
      interval.lowerBound === next.upperBound
    ) {
      return interval.lowerBound
    }
    throw new Error(
      `Intervals ${i} and ${i + 1} share no boundary (non-contiguous partition)`,
    )
  })
}

type WithTransitionFunctions = {
  transitionFunctions?: TransitionFunction[] | null
  intervals?: readonly Interval[]
}

// True only when the variable carries its OWN configs. Since KBE-769 this is
// "has a custom override", NOT "is continuous" -- an inheriting variable is
// equally continuous, it just follows the network default.
//
// KBE-758: this must apply the SAME length check `resolveTransitionFunctions`
// does, or it disagrees with the behavior it claims to describe. A stale-length
// array -- left behind when a state was added or removed -- resolves as inherit,
// so a bare `length > 0` would call such a variable tuned while inference treats
// it as untuned. Pass `intervals` to get the accurate answer; omitting them can
// only ever answer "not custom", which is the safe direction.
export function variableHasCustomTransitionFunctions(
  variable: WithTransitionFunctions,
): boolean {
  const stored = variable.transitionFunctions
  if (!stored || stored.length === 0) return false
  return stored.length === Math.max((variable.intervals?.length ?? 0) - 1, 0)
}

// KBE-768: the band is a percentage of the transition's own threshold, so one
// default is scale-appropriate on every variable -- 5% of 5.6 mmol/L and 5% of
// 140 mg/dL are both sensible, where the absolute default this replaced
// (KBE-762's flat 1.0) was only ever right at one scale.
//
// KBE-769 made the default per-network, so this reads a network row rather than
// a constant. The code constant remains the fallback for the callers that
// genuinely have no network in reach -- Storybook stories and unit tests.
export function resolveDefaultHalfBandPercent(
  network?: NetworkTransitionDefaults | null,
): number {
  return (
    network?.defaultTransitionHalfBandPercent ??
    DEFAULT_TRANSITION_HALF_BAND_PERCENT
  )
}

// The band in the variable's own units: a percentage of the threshold it is
// centred on (KBE-768). `Math.abs` because a band is a width -- on a variable
// whose domain runs negative, ±5% of a threshold at -5 is still ±0.25, not a
// negative span.
//
// A threshold of exactly 0 has no percentage, and yields 0 here. That is left to
// the `isUsableTransitionBand` guard rather than special-cased: the degenerate
// rendering (a hard cutoff) is the honest answer for a band that cannot be
// derived, and a per-variable override is the way out of it.
//
// Multiplied in decimal (KBE-817) because this value is STORED, not just rendered:
// `setTransitionFunctionsForVariables` writes it as the distance, and switching a
// variable from Network default to Symmetrical stores the seeded one. Plain
// multiplication makes 5% of 5.6 into 0.27999999999999997, which does not survive
// the editor's resolve -> store round trip -- so a later edit to one transition
// would rewrite every untouched row's stored value and report a change whose two
// sides render identically.
export function resolveAbsoluteBand(
  halfBandPercent: number,
  threshold: number,
): number {
  return multiplyDecimal(halfBandPercent / 100, Math.abs(threshold))
}

// The 95% value sitting the same distance above the MIDPOINT as `fivePercentValue`
// sits below it -- what Symmetrical mode means, and the number its derived column
// shows while the 5% field is being typed into.
//
// KBE-858 renamed this from `mirrorAboveBoundary`: it mirrors about the stored
// midpoint, which is no longer the interval boundary. Still decimal, because the
// result is displayed, exported and passed to the curve.
export function mirrorAboveMidpoint(
  midpoint: number,
  fivePercentValue: number,
): number {
  return addDecimal(midpoint, subtractDecimal(midpoint, fivePercentValue))
}

export function isSymmetricalTransition(entry: TransitionFunction): boolean {
  return (
    entry.ninetyFivePercentValue === null ||
    entry.ninetyFivePercentValue === undefined
  )
}

// A stored entry's 95% point with Symmetrical mirroring applied -- the single place
// KBE turns its own two-mode storage into the three points everything downstream
// wants. The library takes only the resolved form, so this runs before every call
// into the curve and before every export (KBE-858).
export function transitionNinetyFivePercentValue(
  entry: TransitionFunction,
): number {
  return isSymmetricalTransition(entry)
    ? mirrorAboveMidpoint(entry.midpoint, entry.fivePercentValue)
    : // Non-null by `isSymmetricalTransition`.
      (entry.ninetyFivePercentValue as number)
}

// Whether a STORED entry is internally coherent, mirroring resolved first so a
// Symmetrical row is judged on the curve it actually describes.
//
// This is the write-path guard KBE-858 needs. Under KBE-817's distances,
// bracketing held by construction and a DB CHECK on the sign was the whole
// guarantee; absolute values can be entered in the wrong order, and the failure is
// SILENT rather than loud -- one side goes negative, `isUsableTransitionBand` turns
// false, and the curve degrades to a hard cutoff. The predicate itself is the
// library's, deliberately: it is the one piece of this that Ancestra Health has to
// agree with us about.
export function isValidTransitionFunction(entry: TransitionFunction): boolean {
  return isValidTransition({
    midpoint: entry.midpoint,
    fivePercentValue: entry.fivePercentValue,
    ninetyFivePercentValue: transitionNinetyFivePercentValue(entry),
  })
}

// Whether two entries are the same STORED config -- which includes the mode, so a
// Symmetrical entry and the Skewed entry spelling out its mirror are NOT equal even
// though they fit the same curve.
//
// That is deliberate and predates KBE-858. The mode is what the editor reads back
// to decide whether the 95% column is an input or a derived value, so an equality
// that ignored it would make switching Symmetrical -> Skewed on unchanged numbers a
// no-op save, and the mode would revert on the next reload. Absent and explicitly
// null are the same absence, since only the presence of a 95% value discriminates.
export function isSameTransitionFunction(
  a: TransitionFunction,
  b: TransitionFunction,
): boolean {
  return (
    a.midpoint === b.midpoint &&
    a.fivePercentValue === b.fivePercentValue &&
    (a.ninetyFivePercentValue ?? null) === (b.ninetyFivePercentValue ?? null)
  )
}

// What the network default means at one boundary: the percentage resolved against
// that boundary's own value, mirrored either side of it. An INHERITING variable has
// no stored entry, so it has no midpoint of its own and its curve sits on the
// boundary -- which is what inheriting means.
//
// Positioned in decimal, and not merely cosmetically: switching a variable from
// Network default to a custom mode SEEDS the stored entry from this resolved curve,
// so a noisy position would become a noisy stored value. Plain arithmetic gives
// `5.6 - 0.28 = 5.319999999999999`.
export function resolveTransitionFromPercent(
  boundary: number,
  halfBandPercent: number,
): ResolvedTransition {
  const band = resolveAbsoluteBand(halfBandPercent, boundary)
  return {
    midpoint: boundary,
    fivePercentValue: subtractDecimal(boundary, band),
    ninetyFivePercentValue: addDecimal(boundary, band),
  }
}

// The single point where "inherit" becomes concrete (KBE-769). `stored` is
// `variable.transitionFunctions`: NULL/undefined means follow the network default,
// a non-null array is a custom override. Everything downstream -- the editor chart
// and the diagnosis-time virtual evidence -- consumes the resolved array, so a
// network-default change reaches an inheriting variable through exactly one code
// path and the chart cannot disagree with what inference does.
//
// KBE-858 split what this function does in two. A STORED entry is now self-contained
// and is simply mirrored out to three points -- `boundaries` is not consulted for it
// at all, which is what makes a moved interval bound leave a tuned variable alone.
// Only the INHERIT branch reads a boundary, because a percentage needs a threshold.
//
// `boundaries` are in stored order (as `deriveTransitionBoundaries` returns them),
// and the result is co-indexed with them.
//
// The `usable` line reads a stale-length array as "inherit" where the library
// THROWS on one. KBE-839 settled why that stays, having first recorded it as a
// defect. The caller that can actually hand this function a mismatched pair is the
// EDITOR, resolving its own unsaved state: `ContinuousTransitionSection` pairs a
// locally built array against boundaries derived from the current `intervals` prop,
// and those two disagree transiently while a save is in flight or a state is added
// elsewhere in the dialog. Falling back to the inherited curve there is a mid-edit
// preview that self-heals on the next render, not inference answering from
// substituted data. `if (!entry)` covers a `null` hole the same way.
//
// For a STORED array the invariant is upheld by every write path and by nothing
// below them -- a DB constraint for the LENGTH was written and rejected, see
// `docs/solutions/architecture-decisions/continuous-transition-storage-and-curve.md`
// before reaching for one again. (The per-entry BRACKETING check is a different
// thing and does have a CHECK, since it constrains one row's own fields.)
export function resolveTransitionFunctions(
  stored: readonly TransitionFunction[] | null | undefined,
  defaultHalfBandPercent: number,
  boundaries: readonly number[],
): ResolvedTransition[] {
  const usable = stored && stored.length === boundaries.length ? stored : null
  return boundaries.map((boundary, i) => {
    const entry = usable?.[i]
    if (!entry)
      return resolveTransitionFromPercent(boundary, defaultHalfBandPercent)
    return {
      midpoint: entry.midpoint,
      fivePercentValue: entry.fivePercentValue,
      ninetyFivePercentValue: transitionNinetyFivePercentValue(entry),
    }
  })
}

// Convenience wrapper for the callers that hold intervals rather than boundaries.
// Throws on a non-contiguous partition, like `deriveTransitionBoundaries` --
// callers that run during render should guard (see `TransitionProbabilityChart`).
export function resolveTransitionFunctionsForIntervals(
  stored: readonly TransitionFunction[] | null | undefined,
  defaultHalfBandPercent: number,
  intervals: readonly Interval[],
): ResolvedTransition[] {
  return resolveTransitionFunctions(
    stored,
    defaultHalfBandPercent,
    deriveTransitionBoundaries(intervals),
  )
}

export function intervalsAreDescending(
  intervals: readonly Interval[],
): boolean {
  return (
    getIntervalArrayMonotonicity(intervals as Interval[]) ===
    Monotonicity.descending
  )
}

// KBE-758: comparing STORED transition configs across two networks.
//
// A config belongs to the boundary between two adjacent states, not to a stored
// index -- so index 0 of an ascending array describes a different boundary than
// index 0 of a descending one. Everything that compares two variables' configs
// therefore normalizes to the value-ascending frame and keys by the pair of
// adjacent states, here, once.
//
// The state identity is the caller's: the merge/diff engine passes
// copyReferenceIds (stable across networks with a shared pedigree), while the
// text release-note comparison pairs arbitrary networks and passes state names.
// Hence `stateRefs` rather than a committed id or name.
export type TransitionBoundarySource = {
  intervals: readonly Interval[] | undefined
  stateRefs: readonly string[]
  transitionFunctions: readonly TransitionFunction[] | null | undefined
}

export type StoredTransitionAtBoundary = {
  boundaryKey: string
  lowerStateRef: string
  upperStateRef: string
  transition: TransitionFunction | null
  /** The INTERVAL boundary's own value -- distinct from the entry's stored
   * `midpoint` since KBE-858, and the whole point of the feature is that the two
   * may differ. A formatter shows it so a researcher can see a crossover that has
   * moved away from its bound. Null when the partition is non-contiguous and no
   * boundary can be derived; the comparison itself never needs it, since a stored
   * entry is self-contained. */
  boundary: number | null
}

// The stored array only counts when its length still matches the transition
// count. `resolveTransitionFunctions` treats a stale-length array as inherit, so
// treating it as stored here would report a difference against a variable that
// behaves identically.
function usableStoredTransitions(
  source: TransitionBoundarySource,
): readonly TransitionFunction[] | null {
  const transitionCount = (source.intervals?.length ?? 0) - 1
  if (transitionCount < 1) return null
  const stored = source.transitionFunctions
  if (!stored || stored.length !== transitionCount) return null
  return stored
}

// One entry per boundary, ascending, carrying whatever the variable stores for
// it -- `null` meaning it inherits the network default.
export function storedTransitionsByBoundary(
  source: TransitionBoundarySource,
): StoredTransitionAtBoundary[] {
  const intervals = source.intervals
  if (!intervals || intervals.length < 2) return []

  const descending = intervalsAreDescending(intervals)
  const ascendingRefs = descending
    ? [...source.stateRefs].reverse()
    : source.stateRefs
  const stored = usableStoredTransitions(source)
  const ascendingStored = stored && descending ? [...stored].reverse() : stored
  const ascendingBoundaries = ascendingBoundaryValues(intervals, descending)

  return intervals.slice(0, -1).map((_, i) => ({
    boundaryKey: `${ascendingRefs[i]}->${ascendingRefs[i + 1]}`,
    lowerStateRef: ascendingRefs[i],
    upperStateRef: ascendingRefs[i + 1],
    transition: ascendingStored?.[i] ?? null,
    boundary: ascendingBoundaries?.[i] ?? null,
  }))
}

// The boundary values in the value-ascending frame, or null when the partition is
// non-contiguous. Swallows the throw because this is a reporting path: a network
// whose intervals are corrupt should still produce a readable diff of everything
// else about it.
function ascendingBoundaryValues(
  intervals: readonly Interval[],
  descending: boolean,
): number[] | null {
  try {
    const boundaries = deriveTransitionBoundaries(intervals)
    return descending ? [...boundaries].reverse() : boundaries
  } catch {
    return null
  }
}

export function haveSameStoredTransitions(
  a: TransitionBoundarySource,
  b: TransitionBoundarySource,
): boolean {
  // Neither side has tuned anything, so there is no stored difference whatever
  // their boundaries look like. Without this, a variable that merely gained a
  // state would report a transition change purely from the changed boundary
  // keys, duplicating what the interval and state changes already say.
  if (!usableStoredTransitions(a) && !usableStoredTransitions(b)) return true

  const aByKey = new Map(
    storedTransitionsByBoundary(a).map((entry) => [
      entry.boundaryKey,
      entry.transition,
    ]),
  )
  const bByKey = new Map(
    storedTransitionsByBoundary(b).map((entry) => [
      entry.boundaryKey,
      entry.transition,
    ]),
  )
  if (aByKey.size !== bByKey.size) return false

  for (const [key, aEntry] of aByKey) {
    if (!bByKey.has(key)) return false
    const bEntry = bByKey.get(key) ?? null
    if (!aEntry || !bEntry) {
      if (aEntry !== bEntry) return false
      continue
    }
    if (!isSameTransitionFunction(aEntry, bEntry)) return false
  }
  return true
}

// Which boundary a config belongs to, as one quoted token. The quotes and the
// unicode arrow keep it readable next to the plain-text release note's own
// `from -> to` separator, which would otherwise be impossible to tell apart
// from a boundary written with the same arrow.
export function formatTransitionBoundaryLabel(
  boundary: Pick<StoredTransitionAtBoundary, 'lowerStateRef' | 'upperStateRef'>,
): string {
  return `"${boundary.lowerStateRef}"→"${boundary.upperStateRef}"`
}

// How a stored config reads to a researcher. `null` is "inherit", which is a
// distinct state from any value and must never render as a blank or a zero.
//
// Every number here is stored verbatim since KBE-858 -- no arithmetic, and no need
// for the boundary to make the text readable. The boundary is still taken, and
// still shown, for the one thing that became sayable with this change: a crossover
// that has moved away from the bound it was authored against. That is the record
// that stops "moving an interval bound no longer moves the transition" from being
// an invisible behavior change.
export function formatStoredTransition(
  transition: TransitionFunction | null,
  boundary: number | null,
): string {
  if (!transition) return 'inherits network default'
  const { midpoint, fivePercentValue } = transition
  const points = isSymmetricalTransition(transition)
    ? `50% at ${midpoint}, 5% at ${fivePercentValue}`
    : `50% at ${midpoint}, 5% at ${fivePercentValue}, 95% at ${transitionNinetyFivePercentValue(transition)}`
  if (boundary === null || boundary === midpoint) return points
  return `${points} (boundary at ${boundary})`
}

// The virtual-evidence contract (KBE-654 / KBE-651): given a continuous input
// value, return the probability distribution over the variable's states, in the
// SAME positional order as `intervals`/`stateIds` (the order KBE already sends
// stateCount + the flattened CPT in). This single builder feeds both the
// diagnosis-time virtual-evidence vector and the editor chart, so the curve the
// researcher sees is exactly the one inference uses -- and since KBE-837 it is
// also the one Ancestra Health uses, because the curve itself lives in
// `@ancestralize/bayesian-utils`.
//
// `transitions` are the RESOLVED transitions in stored order (see
// `resolveTransitionFunctions`), each carrying all three of its points.
//
// KBE-858: the library's `MeasurementScale` no longer takes the partition, so
// `intervals` is used for exactly two things here -- checking the count, and
// deriving `descending`. Direction has to be passed because it is NOT recoverable
// from the transitions: a two-state variable has one midpoint and no ordering
// information at all, which is most of the real variables.
//
// Throws on a state/transition-count mismatch -- corrupt data where any produced
// vector would be a silent fabrication. Callers that run during render must guard
// (gotcha 27).
export function buildTransitionStateProbabilities(
  value: number,
  intervals: readonly Interval[],
  transitions: readonly ResolvedTransition[],
): number[] {
  // No intervals is no states, so there is no distribution to return. Guarded here
  // because the library derives its state count as `transitions.length + 1` and
  // would answer `[1]` -- a confident distribution over a variable that has none.
  if (intervals.length === 0) return []
  const expected = intervals.length - 1
  if (transitions.length !== expected) {
    throw new Error(
      `buildTransitionStateProbabilities: expected ${expected} transitions for ${intervals.length} intervals, got ${transitions.length}`,
    )
  }
  return buildTransitionStateProbabilitiesForScale(value, {
    transitions,
    descending: intervalsAreDescending(intervals),
  })
}

export type TransitionFocusWindow = { min: number; max: number }

// The x-range the editor chart plots: tight around the transition boundaries
// rather than the full domain, dropping the flat leading/trailing tails.
// Padding by a couple of transition widths is scale-invariant -- at 2x the width
// a logistic is already past 99.7%, so the curves are visibly flattening at the
// edges whatever units the variable uses. Returned ascending (min < max) since
// the chart always reads low->high.
const FOCUS_WINDOW_PAD_FACTOR = 2

// Used when a transition has no real width to pad by -- a hard cutoff, or a
// boundary of 0 that a percentage cannot resolve against. Padding by a share of
// the domain cannot itself collapse (a valid partition always spans something).
const FOCUS_WINDOW_FALLBACK_DOMAIN_FRACTION = 0.1

export function computeTransitionFocusWindow(
  intervals: readonly Interval[],
  transitions: readonly ResolvedTransition[],
): TransitionFocusWindow | null {
  if (intervals.length <= 1) return null
  if (transitions.length !== intervals.length - 1) return null

  const descending = intervalsAreDescending(intervals)
  const ascendingIntervals = descending ? [...intervals].reverse() : intervals
  const ascendingTransitions = descending
    ? [...transitions].reverse()
    : transitions

  const domainMin = ascendingIntervals[0].lowerBound
  const domainMax = ascendingIntervals[ascendingIntervals.length - 1].upperBound
  const fallbackPad =
    (domainMax - domainMin) * FOCUS_WINDOW_FALLBACK_DOMAIN_FRACTION
  const pad = (width: number): number =>
    FOCUS_WINDOW_PAD_FACTOR *
    (isUsableTransitionBand(width) ? width : fallbackPad)

  // Taken over EVERY transition, not just the first and last. Until KBE-858 the
  // midpoint WAS the interval boundary, so the midpoints were ascending and inside
  // the domain, and the outer two bounded all of them. A stored midpoint is freely
  // typed now: it can sit outside the partition, and crossed midpoints are
  // explicitly legal (gotcha 24). Reading only the ends then produced an INVERTED
  // window -- measured, `{ min: 8, max: 7 }` for two crossed transitions -- which
  // gives the chart a negative width, `Math.log10` of it a NaN, and every one of its
  // 101 sample points an `x` of NaN. The chart is the one thing that communicates a
  // degenerate configuration, so blanking it there is exactly backwards.
  const lower = Math.min(
    ...ascendingTransitions.map(
      (transition) =>
        transition.midpoint -
        pad(transition.midpoint - transition.fivePercentValue),
    ),
  )
  const upper = Math.max(
    ...ascendingTransitions.map(
      (transition) =>
        transition.midpoint +
        pad(transition.ninetyFivePercentValue - transition.midpoint),
    ),
  )

  const min = Math.max(domainMin, lower)
  const max = Math.min(domainMax, upper)
  // Every transition sits outside the partition, so the clamp left nothing. Showing
  // the whole domain keeps the curve visible (saturated, which is what inference
  // does with it) rather than returning null and hiding the chart altogether.
  if (!(min < max)) return { min: domainMin, max: domainMax }
  return { min, max }
}
